"use client";

// ═══════════════════════════════════════════════════════════════
// Imports
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { Bold, Italic, Strikethrough, AlignLeft, AlignCenter, AlignRight, Heading1, Heading2, Heading3 } from "lucide-react";
import { Toggle } from "@/components/ecoBuilder/ui/toggle";
import { Separator } from "@/components/ecoBuilder/ui/separator";
import { type ComponentSchema } from "@/lib/ecoBuilder/schema/types";
import { SpacingSchema } from "@/lib/ecoBuilder/schema/common";
import { cn } from "@/lib/utils";
import { SpacingValue } from "@/components/ecoBuilder/editor/fields/SpacingControl";
import { DimensionValue } from "@/components/ecoBuilder/editor/fields/DimensionsControl";
import { BorderValue } from "@/components/ecoBuilder/editor/fields/BorderControl";

// ═══════════════════════════════════════════════════════════════
// Schema Definition
// ═══════════════════════════════════════════════════════════════

export const TextAtomSchema: ComponentSchema = {
    ...SpacingSchema, // Spread global spacing props
    content: {
        type: "richtext",
        label: "المحتوى",
        placeholder: "أدخل النص هنا...",
        defaultValue: "<p>اضغط للتعديل...</p>",
    },
    align: {
        type: "select",
        label: "المحاذاة",
        defaultValue: "right",
        options: [
            { value: "left", label: "يسار" },
            { value: "center", label: "وسط" },
            { value: "right", label: "يمين" },
        ],
    },
    color: {
        type: "color",
        label: "اللون",
        defaultValue: "#1f2937",
    },
    fontSize: {
        type: "slider",
        label: "حجم الخط",
        defaultValue: 16,
        sliderConfig: { min: 12, max: 128, step: 1, unit: "px" },
    },
    fontWeight: {
        type: "select",
        label: "سُمك الخط",
        defaultValue: "normal",
        options: [
            { value: "normal", label: "عادي" },
            { value: "bold", label: "عريض" },
            { value: "black", label: "سميك جداً" },
        ],
    },
    lineHeight: {
        type: "slider",
        label: "ارتفاع السطر",
        defaultValue: 1.5,
        sliderConfig: { min: 1, max: 3, step: 0.1, unit: "" },
    },
};

export interface TextAtomProps {
    content: string;
    align: "left" | "center" | "right";
    color: string;
    fontSize: number;
    fontWeight: "normal" | "bold" | "black";
    fontFamily?: string;
    lineHeight: number;
    dimensions?: { width: DimensionValue; height: DimensionValue };
    padding?: SpacingValue;
    margin?: SpacingValue;
    border?: BorderValue;
    // Granular Spacing Props
    marginTop?: number;
    marginRight?: number;
    marginBottom?: number;
    marginLeft?: number;
    paddingTop?: number;
    paddingRight?: number;
    paddingBottom?: number;
    paddingLeft?: number;

    onUpdateProps?: (props: Record<string, unknown>) => void;
    className?: string;
    style?: React.CSSProperties;
}


export const TextAtomDefaultProps = {
    content: "<p>اضغط للتعديل...</p>",
    align: "right" as const,
    color: "#1f2937",
    fontSize: 16,
    fontWeight: "normal" as const,
    fontFamily: "var(--font-cairo)",
    lineHeight: 1.5,
};

// ✅ SAFE CLASS STRING (No newlines to prevent Runtime Error)
const EDITOR_CLASSES = "focus:outline-none w-full h-full bg-transparent text-inherit [&_h1]:text-[2.5em] [&_h1]:font-black [&_h1]:leading-tight [&_h1]:mb-4 [&_h2]:text-[2em] [&_h2]:font-bold [&_h2]:leading-tight [&_h2]:mb-3 [&_h3]:text-[1.5em] [&_h3]:font-bold [&_h3]:mb-2 [&_p]:mb-2 [&_p]:leading-relaxed [&_strong]:font-bold [&_strong]:text-inherit";

// ═══════════════════════════════════════════════════════════════
// Floating Toolbar (Portal-based to fix z-index issues)
// ═══════════════════════════════════════════════════════════════

interface FloatingToolbarProps {
    editor: ReturnType<typeof useEditor>;
    containerRef: React.RefObject<HTMLDivElement | null>;
    onUpdateProps?: (props: Record<string, unknown>) => void;
}

function FloatingToolbar({ editor, containerRef, onUpdateProps }: FloatingToolbarProps) {
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

    useEffect(() => {
        if (!editor || !containerRef.current) return;

        const updatePosition = () => {
            const rect = containerRef.current?.getBoundingClientRect();
            if (rect) {
                setPosition({
                    top: rect.bottom + window.scrollY + 8,
                    left: rect.left + window.scrollX + rect.width / 2,
                });
            }
        };

        updatePosition();
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);

        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [editor, containerRef]);

    if (!editor || !position) return null;

    const toggleStyle = "data-[state=on]:bg-black data-[state=on]:text-white hover:bg-gray-100 text-gray-900";

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const toolbar = (
        <div
            className="fixed flex items-center gap-1 p-1 bg-white border border-gray-200 rounded-lg shadow-2xl z-[999999]"
            style={{ top: position.top, left: position.left, transform: "translateX(-50%)" }}
            onMouseDown={handleMouseDown}
        >
            <div className="flex items-center gap-0.5">
                <Toggle size="sm" className={toggleStyle} pressed={editor.isActive("bold")} onPressedChange={() => editor.chain().focus().toggleBold().run()}>
                    <Bold className="w-4 h-4" />
                </Toggle>
                <Toggle size="sm" className={toggleStyle} pressed={editor.isActive("italic")} onPressedChange={() => editor.chain().focus().toggleItalic().run()}>
                    <Italic className="w-4 h-4" />
                </Toggle>
                <Toggle size="sm" className={toggleStyle} pressed={editor.isActive("strike")} onPressedChange={() => editor.chain().focus().toggleStrike().run()}>
                    <Strikethrough className="w-4 h-4" />
                </Toggle>
            </div>

            <Separator orientation="vertical" className="h-6 mx-1" />

            <div className="flex items-center gap-0.5">
                <Toggle size="sm" className={toggleStyle} pressed={editor.isActive("heading", { level: 1 })} onPressedChange={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
                    <Heading1 className="w-4 h-4" />
                </Toggle>
                <Toggle size="sm" className={toggleStyle} pressed={editor.isActive("heading", { level: 2 })} onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
                    <Heading2 className="w-4 h-4" />
                </Toggle>
                <Toggle size="sm" className={toggleStyle} pressed={editor.isActive("heading", { level: 3 })} onPressedChange={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
                    <Heading3 className="w-4 h-4" />
                </Toggle>
            </div>

            <Separator orientation="vertical" className="h-6 mx-1" />

            <div className="flex items-center gap-0.5">
                <Toggle size="sm" className={toggleStyle} pressed={editor.isActive({ textAlign: 'right' })} onPressedChange={() => {
                    editor.chain().focus().setTextAlign('right').run();
                    onUpdateProps?.({ align: 'right' });
                }}>
                    <AlignRight className="w-4 h-4" />
                </Toggle>
                <Toggle size="sm" className={toggleStyle} pressed={editor.isActive({ textAlign: 'center' })} onPressedChange={() => {
                    editor.chain().focus().setTextAlign('center').run();
                    onUpdateProps?.({ align: 'center' });
                }}>
                    <AlignCenter className="w-4 h-4" />
                </Toggle>
                <Toggle size="sm" className={toggleStyle} pressed={editor.isActive({ textAlign: 'left' })} onPressedChange={() => {
                    editor.chain().focus().setTextAlign('left').run();
                    onUpdateProps?.({ align: 'left' });
                }}>
                    <AlignLeft className="w-4 h-4" />
                </Toggle>
            </div>
        </div>
    );

    if (typeof window !== "undefined") {
        return createPortal(toolbar, document.body);
    }
    return null;
}

// ═══════════════════════════════════════════════════════════════
// TextAtom Component
// ═══════════════════════════════════════════════════════════════

export function TextAtom({
    content = TextAtomDefaultProps.content,
    align = TextAtomDefaultProps.align,
    color = TextAtomDefaultProps.color,
    fontSize = TextAtomDefaultProps.fontSize,
    fontWeight = TextAtomDefaultProps.fontWeight,
    fontFamily = TextAtomDefaultProps.fontFamily,
    lineHeight = TextAtomDefaultProps.lineHeight,
    // Spacing props passed via style object from Universal Schema
    // padding, margin, border handled by style prop
    onUpdateProps,
    className,
    style,
}: Partial<TextAtomProps>) {
    const [isEditing, setIsEditing] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const isLocalUpdate = useRef(false); // Lock for sync

    const fontWeightMap = { normal: 400, bold: 700, black: 900 };
    const numericFontWeight = fontWeightMap[fontWeight || 'normal'] || 400;

    const editor = useEditor({
        immediatelyRender: false,
        extensions: [
            StarterKit,
            TextAlign.configure({ types: ["heading", "paragraph"] }),
        ],
        content: content,
        editable: isEditing,
        editorProps: {
            attributes: {
                class: EDITOR_CLASSES,
                style: `text-align: ${align};`,
            },
        },
        onUpdate: () => {
            isLocalUpdate.current = true; // We are typing
        },
        onBlur: ({ editor }) => {
            setIsEditing(false);
            isLocalUpdate.current = false;
            onUpdateProps?.({ content: editor.getHTML() }); // Save on exit
        }
    });

    // 🔄 Smart Sync: Only update from props if we aren't typing
    useEffect(() => {
        if (!editor || isEditing || isLocalUpdate.current) return;

        if (content !== editor.getHTML()) {
            editor.commands.setContent(content);
        }
    }, [content, editor, isEditing]);

    // Wrapper Styles: Combine specific text styles with universal box-model styles passed via `style` prop
    const wrapperStyle: React.CSSProperties = {
        textAlign: align,
        color: color,
        fontSize: `${fontSize}px`,
        fontWeight: numericFontWeight,
        fontFamily: fontFamily,
        lineHeight,
        ...style,
    };

    return (
        <div
            ref={containerRef}
            className={cn("relative group transition-all duration-200", className)}
            style={wrapperStyle}
            onDoubleClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
                editor?.setEditable(true);
                editor?.commands.focus();
            }}
        >
            <div
                className={cn("w-full h-full", isEditing ? "cursor-text relative z-[50]" : "cursor-move")}
                onMouseDown={(e) => isEditing && e.stopPropagation()}
            >
                <EditorContent editor={editor} />

                {editor && isEditing && (
                    <FloatingToolbar editor={editor} containerRef={containerRef} onUpdateProps={onUpdateProps} />
                )}
            </div>

            {!isEditing && (
                <div className="absolute inset-0 border-2 border-transparent hover:border-blue-200 pointer-events-none rounded transition-colors" />
            )}
        </div>
    );
}

export default TextAtom;
